import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getMyBookmarkedRequests, toggleBookmark } from '../lib/api';
import { MapPin, Clock, Bookmark, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { getRateTypeLabel } from '../lib/utils';
import { readVenueName } from '../constants/profileContract';

function formatEventDate(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return {
    day: d.getDate(),
    month: d.toLocaleDateString('en', { month: 'short' }).toUpperCase(),
    weekday: d.toLocaleDateString('en', { weekday: 'short' }),
  };
}

export default function SavedRequestsPage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await getMyBookmarkedRequests();
      setRequests(res.data);
    } catch {
      toast.error('Failed to load saved requests');
    } finally {
      setLoading(false);
    }
  };

  const handleUnsave = async (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await toggleBookmark(id);
      setRequests(prev => prev.filter(r => r.id !== id));
      toast.success('Removed from saved');
    } catch {
      toast.error('Failed to remove');
    }
  };

  if (loading) {
    return (
      <div className="page-container-lg">
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-28 bg-[#151515] animate-pulse rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="page-container-lg">
      <div className="mb-6">
        <Link to="/" className="inline-flex items-center gap-1 text-white/40 hover:text-white/60 text-sm mb-3 transition-colors" data-testid="back-btn">
          <ArrowLeft className="h-4 w-4" />
          Back
        </Link>
        <h1 className="section-header text-2xl text-white">Saved Requests</h1>
        <p className="text-xs text-white/40 mt-1">{requests.length} saved</p>
      </div>

      {requests.length === 0 ? (
        <div className="text-center py-16">
          <Bookmark className="h-8 w-8 text-white/20 mx-auto mb-3" />
          <p className="text-white/40 text-sm">No saved requests yet</p>
          <Link to="/" className="mt-3 inline-block text-[var(--s8-amber)] hover:text-white text-sm">
            Browse requests
          </Link>
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          {requests.map((request, index) => {
            const { day, month } = formatEventDate(request.eventDate);
            return (
              <Link
                key={request.id}
                to={`/requests/${request.reference || request.id}`}
                className="block group"
                style={{ animationDelay: `${index * 80}ms` }}
                data-testid={`saved-request-card-${request.id}`}
              >
                <div className="card-press relative rounded-2xl border border-white/10 shadow-md overflow-hidden bg-gradient-to-br from-[#1e1915] to-[#171411]">
                  <div className="absolute left-0 top-0 h-full w-[3px] bg-[var(--s8-amber)]" />
                  <div className="pl-5 pr-5 py-5 overflow-hidden">
                    <div className="flex gap-3">
                      <div className="flex flex-col items-center justify-center min-w-[44px] shrink-0">
                        <span className="text-white font-semibold text-xl">{day}</span>
                        <span className="text-white/60 text-[10px] uppercase tracking-wide">{month}</span>
                      </div>
                      <div className="flex items-center justify-center shrink-0">
                        <div className="h-[85%] w-px border-l border-dotted border-white/15" />
                      </div>
                      <div className="flex-1 min-w-0 overflow-hidden">
                        <div className="grid grid-cols-[1fr_auto] gap-3 mb-2">
                          <h3 className="text-white font-semibold text-lg leading-snug min-w-0 line-clamp-2">
                            {request.title?.replace('[DEMO] ', '')}
                          </h3>
                          <div className="text-right shrink-0 whitespace-nowrap">
                            {request.budgetAmount ? (
                              <>
                                <span className="text-white font-medium text-lg">{'\u20AC'}{request.budgetAmount}</span>
                                <span className="text-white/60 text-sm block">
                                  {getRateTypeLabel(request.rateType)}
                                </span>
                              </>
                            ) : (
                              <span className="text-white/60 text-sm">TBD</span>
                            )}
                          </div>
                        </div>
                        <div className="grid grid-cols-[1fr_auto] gap-3 items-center">
                          <span className="flex items-center gap-1.5 text-white/70 text-sm min-w-0">
                            <MapPin className="h-3.5 w-3.5 shrink-0" />
                            <span className="line-clamp-1">
                              {[readVenueName(request), request.city].filter(Boolean).join(' \u00B7 ')}
                            </span>
                          </span>
                          {request.startTime && request.endTime && (
                            <span className="flex items-center gap-1.5 text-white/70 text-sm shrink-0 whitespace-nowrap">
                              <Clock className="h-3.5 w-3.5 shrink-0" />
                              <span>{request.startTime}–{request.endTime}</span>
                            </span>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={(e) => handleUnsave(e, request.id)}
                        className="p-1 rounded hover:bg-white/10 transition-colors shrink-0 self-start"
                        data-testid={`unsave-${request.id}`}
                      >
                        <Bookmark className="h-4 w-4 text-white fill-white" />
                      </button>
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
